import React, { Component } from 'react'

import { 
    getAssetName, 
    generateEvents,
    getClientDisplayName,
    getCurrentUser,
} from 'utils/utils'
import FontAwesome from 'react-fontawesome'
import moment from 'moment'
import Table from 'components/Table/Table'
import { Link } from 'react-router-dom'
import { withApollo, compose, graphql } from 'react-apollo'
import Loader from 'components/Loader/Loader'
import SwitchInput from 'components/Inputs/SwitchInput'
import SelectInput from 'components/Inputs/SelectInput'

import Button from 'components/Button/Button'

import { OPEN_MODAL } from 'graphql/modal-graphql'
import { cloneDeep } from 'lodash'

class EventsOverview extends Component {
    state = {
        daysToShow: 30,
        selectedBroker: 'all', // Will be updated in componentDidMount if current user exists in brokers
        showDismissedEvents: false
    }

    componentDidMount() {
        // Set the default broker to the current user after component mounts
        this.setDefaultBrokerToCurrentUser();
    }
    
    componentDidUpdate(prevProps) {
        // Update broker selection if people or companies change and current user is now in the list
        if (prevProps.people !== this.props.people || prevProps.companies !== this.props.companies) {
            this.setDefaultBrokerToCurrentUser();
        }
    }
    
    // Function to set default broker to current user if they exist in the broker list
    setDefaultBrokerToCurrentUser = () => {
        const currentUser = getCurrentUser(this.props.client);
        if (currentUser) {
            const brokers = this.getBrokersList();
            // Check if the current user exists in the brokers list
            const currentUserBroker = brokers.find(broker => broker.id === currentUser.id);
            if (currentUserBroker && this.state.selectedBroker === 'all') {
                // Only update if the current selection is still 'all' (not changed by user)
                this.setState({
                    selectedBroker: currentUser.id
                });
            }
        }
    }

    editEvent = event => {
        let eventObject = cloneDeep(event)

        if (event.person) {
            eventObject.personId = event.person.id
        } else if (event.company) {
            eventObject.companyId = event.company.id
        }

        this.props.client.mutate({
            mutation: OPEN_MODAL,
            variables: {
                operation: 'edit',
                dataType: 'event',
                isVisible: true,
                object: eventObject
            }
        })
    }

    getBrokersList = () => {
        const { people, companies } = this.props;
        
        // Get all unique brokers from people and companies
        const brokersMap = new Map();
        
        // Add default option
        brokersMap.set('all', { id: 'all', firstName: 'All', lastName: 'Brokers' });
        
        // Add brokers from people
        people && people.forEach(person => {
            if (person.broker) {
                brokersMap.set(person.broker.id, person.broker);
            }
        });
        
        // Add brokers from companies
        companies && companies.forEach(company => {
            if (company.broker) {
                brokersMap.set(company.broker.id, company.broker);
            }
        });
        
        return Array.from(brokersMap.values());
    }

    openModal = asset => {
        this.props.openModal({
            variables: {
                operation: 'edit',
                dataType: 'asset',
                isVisible: true,
                object: asset
            }
        })
    }

    getRowData = (key, event) => {
        // Add styling for dismissed events
        const isDismissed = event.eventObject && event.eventObject.isDismissed;
        const dismissedStyle = isDismissed ? { 
            textDecoration: 'line-through', 
            color: '#ae8484', 
        } : {};

        let response

        switch (key) {
            case 'client':
                let clients = []

                if (event.eventObject.people || event.eventObject.companies) {
                    let people = event.eventObject.people
                    let companies = event.eventObject.companies

                    let tempClients = people.concat(companies)

                    tempClients.forEach(client => {
                        if (client.__typename == 'LiabilityApplicant') {
                            clients.push(client.person ? client.person : client.company)
                        } else {
                            clients.push(client)
                        }
                    })
                } else {
                    if (event.eventObject.person) {
                        clients = [event.eventObject.person]
                    } else {
                        clients = [event.eventObject.company]
                    }
                }

                response = (
                    <ul className="table-list">
                        {
                            clients.map((client, index) => {
                                if(!client){
                                    return null
                                }

                                if (client.__typename == 'Person') {
                                    return (
                                        <li key={index} style={dismissedStyle}>
                                            <Link to={"/clients/people/" + client.id}>
                                                <img src="/images/icons/person.png" />
                                                { getClientDisplayName(client) }
                                            </Link>
                                        </li>
                                    )
                                } else {
                                    return (
                                        <li key={index} style={dismissedStyle}>
                                            <Link to={"/clients/companies/" + client.id}>
                                                <img src="/images/icons/company.png" />
                                                {client.name}
                                            </Link>
                                        </li>
                                    )
                                }
                            })
                        }
                    </ul>
                )
                break
            case 'details':
                response = (
                    <span style={dismissedStyle}>
                        <span>
                            {event.title}
                        </span>
                        {
                            event.assets ? event.assets.map((asset, index) => {
                                if (getAssetName(asset)) {
                                    return (
                                        <a key={index} onClick={e => { e.stopPropagation(); this.openModal(asset) }} style={dismissedStyle}>
                                            <img src="/images/icons/company.png" />
                                            { getAssetName(asset) }
                                        </a>
                                    )
                                } else {
                                    return null
                                }
                            }) : null
                        }
                    </span>
                )
                break
            case 'broker':
                const userName = this.getEventBrokerName(event);
                
                response = (
                    <span style={dismissedStyle}>
                        {userName}
                    </span>
                )
                break
            case 'date':
                const today = moment().format()
                const week = moment().add(7, 'days')

                function isWithinAWeek(momentDate) {
                    return momentDate.isBefore(week)
                }
                function isAfterAWeek(momentDate) {
                    return momentDate.isAfter(week)
                }
                function isExpired(momentDate) {
                    return momentDate.isBefore(today)
                }

                let spanClass

                if (isWithinAWeek(event.date)) {
                    spanClass = 'within-week'
                } 
                if (isAfterAWeek(event.date)) {
                    spanClass = 'after-week'
                } 
                if (isExpired(event.date)) {
                    spanClass = 'expired'
                } 

                response = (
                    <span className={spanClass} style={dismissedStyle}>
                        <FontAwesome name="calendar-o" />
                        {event.formattedDate}
                    </span>
                )
                break
            case 'view':
                response = (
                    <span style={dismissedStyle}>
                        <Button label="View event" level="x-low" callback={() => {this.editEvent(event.eventObject)}} />
                    </span>
                )
                break
        }

        return {
            label: response
        }
    }

    handleChange = daysToShow => {
        this.setState({
            daysToShow
        })
    }

    handleBrokerChange = selectedBroker => {
        this.setState({
            selectedBroker
        })
    }

    handleShowDismissedEventsChange = showDismissedEvents => {
        this.setState({
            showDismissedEvents
        })
    }

    getEventBrokerName = event => {
        // For all events, try to get the owner of the associated client
        if (event.eventObject) {
            // Try to get the client (person or company) associated with the event
            let client = null;
            if (event.eventObject.person) {
                client = event.eventObject.person;
            } else if (event.eventObject.company) {
                client = event.eventObject.company;
            } else if (event.eventObject.people && event.eventObject.people.length > 0) {
                // For liability events, get the first person
                client = event.eventObject.people[0].person || event.eventObject.people[0];
            } else if (event.eventObject.companies && event.eventObject.companies.length > 0) {
                // For liability events, get the first company
                client = event.eventObject.companies[0].company || event.eventObject.companies[0];
            }
            
            // If we found a client and it has a broker, use the broker's name
            if (client && client.broker) {
                return getClientDisplayName(client.broker);
            } else {
                // If we couldn't get the broker from the event object directly,
                // try to find the client in the props and get the broker from there
                if (event.eventObject.people && event.eventObject.people.length > 0 && this.props.people) {
                    const personId = event.eventObject.people[0].person ? event.eventObject.people[0].person.id : event.eventObject.people[0].id;
                    const person = this.props.people.find(p => p.id === personId);
                    if (person && person.broker) {
                        return getClientDisplayName(person.broker);
                    }
                } else if (event.eventObject.companies && event.eventObject.companies.length > 0 && this.props.companies) {
                    const companyId = event.eventObject.companies[0].company ? event.eventObject.companies[0].company.id : event.eventObject.companies[0].id;
                    const company = this.props.companies.find(c => c.id === companyId);
                    if (company && company.broker) {
                        return getClientDisplayName(company.broker);
                    }
                } else if (event.eventObject.person && this.props.people) {
                    const person = this.props.people.find(p => p.id === event.eventObject.person.id);
                    if (person && person.broker) {
                        return getClientDisplayName(person.broker);
                    }
                } else if (event.eventObject.company && this.props.companies) {
                    const company = this.props.companies.find(c => c.id === event.eventObject.company.id);
                    if (company && company.broker) {
                        return getClientDisplayName(company.broker);
                    }
                }
            }
        }
        
        // For system events created by Zamm, return Zamm if no client broker found
        if (event.eventObject && event.eventObject.isSystem && event.addedBy === 'Zamm') {
            return 'Zamm';
        }
        
        // For custom events, return the creator if no client broker found
        return event.addedBy || 'Unknown User';
    }

    filterDismissedEvents = events => {
        return events.filter(event => {
            // Filter out dismissed events only if the toggle is off (default behavior)
            if(!this.state.showDismissedEvents && event.eventObject.isDismissed){
                return false;
            }

            // Filter by days to show
            // For dismissed events, we want to show events within the past X days
            // For non-dismissed events, we want to show events within the future X days
            let daysBetween = event.date.diff(moment(), 'days');
            
            if (event.eventObject.isDismissed) {
                // For dismissed events, show events from the past X days to today
                if (daysBetween < -this.state.daysToShow || daysBetween > 0) {
                    return false;
                }
            } else {
                // For non-dismissed events, show events from today to the future X days
                if (daysBetween > this.state.daysToShow || daysBetween < 0) {
                    return false;
                }
            }

            // Apply broker filter
            if (this.state.selectedBroker !== 'all') {
                // Get the broker name for this event based on the client
                const eventBrokerName = this.getEventBrokerName(event);
                
                // Find the selected broker's display name
                const selectedBroker = this.getBrokersList().find(b => b.id === this.state.selectedBroker);
                const selectedBrokerName = selectedBroker ? getClientDisplayName(selectedBroker) : '';
                
                // Filter by broker
                if (eventBrokerName !== selectedBrokerName) {
                    return false;
                }
            }

            return true;
        });
    }

    render() {
        const { 
            liabilities, 
            companies, 
            people, 
            isLoading 
        } = this.props

        const {
            daysToShow,
            selectedBroker,
            showDismissedEvents
        } = this.state

        // Generate all events first
        let upcomingEvents = generateEvents(liabilities, people, companies, 'all', 'overview');
        
        // For better performance, pre-filter events to only those within the required date range
        // This follows the same logic as in filterDismissedEvents but as a pre-filter
        let eventsToProcess = upcomingEvents.events.filter(event => {
            // Filter by days to show following the same logic as in filterDismissedEvents
            // For dismissed events, we want to show events within the past X days
            // For non-dismissed events, we want to show events within the future X days
            let daysBetween = event.date.diff(moment(), 'days');
            
            if (event.eventObject.isDismissed) {
                // For dismissed events, show events from the past X days to today
                if (daysBetween < -this.state.daysToShow || daysBetween > 0) {
                    return false;
                }
            } else {
                // For non-dismissed events, show events from today to the future X days
                if (daysBetween > this.state.daysToShow || daysBetween < 0) {
                    return false;
                }
            }
            
            return true;
        });
        
        // Update upcomingEvents to only contain the pre-filtered events
        upcomingEvents = {
            events: eventsToProcess,
            liabilities: upcomingEvents.liabilities
        };
        
        // Get brokers list for the dropdown
        const brokers = this.getBrokersList();
        const brokerOptions = brokers.map(broker => ({
            value: broker.id,
            label: broker.id === 'all' ? 'All Brokers' : `${broker.firstName} ${broker.lastName}`
        }));

        this.tableData = {
            rows: this.filterDismissedEvents(upcomingEvents.events),
            columns: [
                {
                    label: 'Client',
                    type: 'value',
                    key: 'client'
                },
                {
                    label: 'Details',
                    type: 'value',
                    key: 'details'
                },
                {
                    label: 'Date',
                    type: 'value',
                    key: 'date'
                },
                {
                    label: 'Broker',
                    type: 'value',
                    key: 'broker'
                },
                {
                    label: '',
                    type: 'value',
                    key: 'view'
                }
            ]
        }

        let switchOptions = [
            {
                label: '30 days',
                value: 30
            },{
                label: '60 days',
                value: 60
            },{
                label: '90 days',
                value: 90
            }
        ]

        const dismissedToggleOptions = [
            {
                label: 'Hide Dismissed',
                value: false
            }, {
                label: 'Show Dismissed',
                value: true
            }
        ]

        return (
            <div className="dashboard-widget events-overview">
    
                <div className="heading">
                    <div className="filter">
                        <div className="input-wrap">
                            <SelectInput 
                                changeCallback={value => {this.handleBrokerChange(value)}} 
                                values={brokerOptions} 
                                value={selectedBroker}  
                            />
                        </div>
                        <SwitchInput 
                            changeCallback={value => {this.handleChange(value)}} 
                            values={switchOptions} 
                            value={daysToShow}  
                        />
                        <SwitchInput 
                            changeCallback={value => {this.handleShowDismissedEventsChange(value)}} 
                            values={dismissedToggleOptions} 
                            value={showDismissedEvents}  
                        />
                    </div>
                </div>
            <div className="heading">
                    <h3>
                        Upcoming events
                    </h3>
                </div>
                <div className="table-container">
                    <Loader 
                        isLoading={isLoading} 
                        showText={true} 
                    />
                    <Table 
                        onlyShow="head" 
                        columns={this.tableData.columns} 
                        data={this.tableData.rows} 
                        getter={this.getRowData} 
                        shouldPaginate={false}
                    />
                    <div className={isLoading ? "table-overflow-container loading" : "table-overflow-container"}>
                        <Table 
                            onlyShow="body" 
                            columns={this.tableData.columns} 
                            data={this.tableData.rows} 
                            getter={this.getRowData} 
                            shouldPaginate={false}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

export default withApollo(compose(graphql(OPEN_MODAL, { name: 'openModal' }))(EventsOverview))